import React from 'react';
import { bgClass, fullscreenClasses } from '../libs/css';
import { useScreenClass, SCREEN_CLASSES } from './use-screen-class';
import type { ScreenClass } from './use-screen-class';
import cc from 'classcat';

const GUTTER_WIDTH = 26;

const CONTAINER_MAX_WIDTHS: Partial<Record<ScreenClass, number>> = {
  sm: 540,
  md: 720,
  lg: 960,
  xl: 1140,
  xxl: 1540
};

export interface ContainerProps {
  children?: React.ReactNode;
  className?: string;
  component?: React.ElementType;
  fluid?: boolean;
  style?: React.CSSProperties;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  xxl?: number;
  fullscreen?: boolean;
  bg?: string;
  onClick?: React.MouseEventHandler;
}

const Container: React.FC<ContainerProps> = ({
  children,
  className,
  component: Component = 'div',
  fluid,
  style,
  xs,
  sm,
  md,
  lg,
  xl,
  xxl,
  fullscreen = false,
  bg,
  onClick
}) => {
  const screenClass = useScreenClass();
  const halfGutter = GUTTER_WIDTH / 2;

  const containerStyle: React.CSSProperties = {
    boxSizing: 'border-box',
    position: 'relative',
    paddingLeft: `${halfGutter}px`,
    paddingRight: `${halfGutter}px`,
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    ...style
  };

  if (!fluid) {
    // Apply max-width from explicit breakpoint props or defaults
    const breakpointOverrides = { xs, sm, md, lg, xl, xxl };
    let maxWidth: number | undefined;

    const currentIndex = SCREEN_CLASSES.indexOf(screenClass);

    if (currentIndex !== -1) {
      for (let i = currentIndex; i >= 0; i -= 1) {
        const sc = SCREEN_CLASSES[i];
        if (breakpointOverrides[sc] !== undefined) {
          maxWidth = breakpointOverrides[sc];
          break;
        }
      }
    }

    if (maxWidth === undefined) {
      maxWidth = CONTAINER_MAX_WIDTHS[screenClass];
    }

    if (maxWidth !== undefined) {
      containerStyle.maxWidth = `${maxWidth}px`;
    }
  }

  const classes = cc([
    bgClass(bg),
    className,
    fullscreenClasses(fullscreen),
    {
      'cursor-pointer': onClick
    }
  ]);

  return (
    <Component className={classes} style={containerStyle} onClick={onClick}>
      {children}
    </Component>
  );
};

export default Container;
